import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, CheckCircle2, Loader2, X, Clock } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import api, { formatApiErrorDetail } from "@/lib/api";

const emptyProg = { name: "", type: "percentage", fixed_amount: 0, percentage: 15, bonus: 0, conditions: "", active: true, tiers: [{ up_to: "", percentage: "" }], is_campaign: false, starts_at: "", ends_at: "", campaign_bonus: 0 };

export default function AdminPrograms() {
  const [programs, setPrograms] = useState([]);
  const [form, setForm] = useState({ ...emptyProg });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/programs").then((r) => setPrograms(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const addTier = () => setForm({ ...form, tiers: [...(form.tiers || []), { up_to: "", percentage: "" }] });
  const updateTier = (i, k, v) => { const t = [...form.tiers]; t[i] = { ...t[i], [k]: v }; setForm({ ...form, tiers: t }); };
  const removeTier = (i) => setForm({ ...form, tiers: form.tiers.filter((_, idx) => idx !== i) });

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/programs", {
        ...form,
        fixed_amount: parseFloat(form.fixed_amount) || 0,
        percentage: parseFloat(form.percentage) || 0,
        bonus: parseFloat(form.bonus) || 0,
      });
      toast.success("Programma creato.");
      setForm({ ...emptyProg });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <DashboardLayout title="Programmi merito fidelizzazione" admin>
      <p className="text-sm text-slate-500 mb-6 max-w-2xl">
        Configura le regole del merito fidelizzazione senza modificare l'applicazione. La percentuale
        (8-12%) viene concordata con il proprietario in fase di incarico sulla singola pratica; qui
        imposti i valori di default e le eventuali campagne.
      </p>

      <div className="grid gap-6 lg:grid-cols-2">
        <form onSubmit={create} className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
          <h2 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2"><Plus className="h-5 w-5" /> Nuovo programma</h2>
          <div>
            <label className="text-sm font-medium text-slate-700">Nome</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="prog-name"
              className="mt-1.5 w-full rounded-lg border border-slate-200 px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Tipo</label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="prog-type"
              className="mt-1.5 w-full rounded-lg border border-slate-200 px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none">
              <option value="percentage">Percentuale sul valore</option>
              <option value="fixed">Importo fisso</option>
              <option value="tiered">Scaglioni</option>
            </select>
          </div>
          {form.type === "tiered" ? (
            <div>
              <label className="text-sm font-medium text-slate-700">Scaglioni (in ordine crescente)</label>
              <div className="mt-2 space-y-2">
                {form.tiers.map((t, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input type="number" value={t.up_to} onChange={(e) => updateTier(i, "up_to", e.target.value)} data-testid={`tier-upto-${i}`}
                      placeholder="Fino a € (vuoto = oltre)" className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none" />
                    <input type="number" step="0.1" value={t.percentage} onChange={(e) => updateTier(i, "percentage", e.target.value)} data-testid={`tier-pct-${i}`}
                      placeholder="%" className="w-20 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none" />
                    <button type="button" onClick={() => removeTier(i)} className="text-slate-400 hover:text-rose-600"><X className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
              <button type="button" onClick={addTier} data-testid="add-tier-btn" className="mt-2 inline-flex items-center gap-1 text-sm text-[#8a6d0f] font-medium"><Plus className="h-4 w-4" /> Aggiungi scaglione</button>
            </div>
          ) : (
            form.type === "percentage" ? (
              <div>
                <label className="text-sm font-medium text-slate-700">Percentuale (%)</label>
                <input type="number" step="0.1" min="8" max="12" value={form.percentage} onChange={(e) => setForm({ ...form, percentage: e.target.value })} data-testid="prog-percentage"
                  className="mt-1.5 w-full rounded-lg border border-slate-200 px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none" />
              </div>
            ) : (
              <div>
                <label className="text-sm font-medium text-slate-700">Importo fisso (€)</label>
                <input type="number" value={form.fixed_amount} onChange={(e) => setForm({ ...form, fixed_amount: e.target.value })} data-testid="prog-fixed"
                  className="mt-1.5 w-full rounded-lg border border-slate-200 px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none" />
              </div>
            )
          )}
          <div>
            <label className="text-sm font-medium text-slate-700">Bonus (€)</label>
            <input type="number" value={form.bonus} onChange={(e) => setForm({ ...form, bonus: e.target.value })} data-testid="prog-bonus"
              className="mt-1.5 w-full rounded-lg border border-slate-200 px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none" />
          </div>

          <div className="rounded-lg border border-slate-200 p-4">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer">
              <input type="checkbox" checked={form.is_campaign} onChange={(e) => setForm({ ...form, is_campaign: e.target.checked })} data-testid="prog-campaign" className="h-4 w-4 accent-[#D4AF37]" />
              <Clock className="h-4 w-4 text-[#8a6d0f]" /> Campagna a tempo (bonus extra nel periodo)
            </label>
            {form.is_campaign && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500">Dal</label>
                  <input type="date" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} data-testid="prog-starts" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none" />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Al</label>
                  <input type="date" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} data-testid="prog-ends" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-slate-500">Bonus campagna (€)</label>
                  <input type="number" value={form.campaign_bonus} onChange={(e) => setForm({ ...form, campaign_bonus: e.target.value })} data-testid="prog-campaign-bonus" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none" />
                </div>
              </div>
            )}
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Condizioni di maturazione</label>
            <textarea rows={2} value={form.conditions} onChange={(e) => setForm({ ...form, conditions: e.target.value })} data-testid="prog-conditions"
              className="mt-1.5 w-full rounded-lg border border-slate-200 px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-5 w-5 accent-[#D4AF37]" data-testid="prog-active" />
            Imposta come programma attivo
          </label>
          <button disabled={saving} data-testid="prog-create-btn" className="btn-gold rounded-full px-6 py-3 font-semibold inline-flex items-center gap-2 disabled:opacity-60">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Crea programma
          </button>
        </form>

        <div className="space-y-4">
          <h2 className="font-display text-lg font-semibold text-slate-900">Programmi esistenti</h2>
          {programs.length === 0 && <p className="text-slate-400 text-sm">Nessun programma.</p>}
          {programs.map((p) => (
            <div key={p.id} className={`rounded-2xl border p-5 ${p.active ? "border-[#D4AF37]/50 bg-[#D4AF37]/5" : "border-slate-200 bg-white"}`} data-testid={`program-${p.id}`}>
              <div className="flex items-center justify-between">
                <p className="font-display font-medium text-slate-900">{p.name}</p>
                <div className="flex items-center gap-2">
                  {p.is_campaign && <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"><Clock className="h-3 w-3" /> Campagna</span>}
                  {p.active && <span className="inline-flex items-center gap-1 text-xs font-medium text-[#8a6d0f]"><CheckCircle2 className="h-4 w-4" /> Attivo</span>}
                </div>
              </div>
              <p className="mt-2 text-sm text-slate-600">
                {p.type === "tiered"
                  ? `Scaglioni: ${(p.tiers || []).map((t) => `${t.up_to != null ? "≤€" + t.up_to : "oltre"} → ${t.percentage}%`).join(", ")}`
                  : p.type === "percentage" ? `${p.percentage}% del valore` : `€ ${p.fixed_amount} fisso`}
                {p.bonus ? ` + € ${p.bonus} bonus` : ""}
                {p.is_campaign && p.campaign_bonus ? ` + € ${p.campaign_bonus} campagna` : ""}
              </p>
              {p.conditions && <p className="mt-1 text-xs text-slate-400">{p.conditions}</p>}
            </div>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
